import React from 'react'
import { useParams } from 'react-router-dom'
import { Grid, GridItem } from '@which/seatbelt'

import type { NewsListingVerticalQuery } from '../../generated/frontend'
import {
  useNewsListingVerticalLazyQuery,
  useNewsListingVerticalQuery,
} from '../../generated/frontend'
import type { PageParams } from '../../routes'
import { useBackStack } from '../../shared/BrowserBackStackProvider'
import { ArticleListingHeader } from '../../shared/components/ArticleListingHeader/ArticleListingHeader'
import { Breadcrumb } from '../../shared/components/Breadcrumb/Breadcrumb'
import { ErrorComponent } from '../../shared/components/Error/ErrorComponent'
import { PageError } from '../../shared/components/Error/PageError'
import { FetchMore } from '../../shared/components/FetchMore/FetchMore'
import { GroupedArticles } from '../../shared/components/GroupedArticles/GroupedArticles'
import { Loader } from '../../shared/components/Loader/Loader'
import { ResubscribeBannerWrapper } from '../../shared/components/ResubscribeBannerWrapper/ResubscribeBannerWrapper'
import { getDataLayerScripts } from '../../shared/data-layer'
import { getDFPScripts } from '../../shared/dfp'
import { useBaseMetaTags } from '../../shared/hooks/useBaseMetaTags'
import { useFullUrl } from '../../shared/hooks/useFullUrl'
import type { PageInfo } from '../../shared/types/PageInfo'
import { getPreviousUrl } from '../../shared/utils/getPreviousUrl/getPreviousUrl'
import { removeFalsy } from '../../shared/utils/remove-falsy'
import { removeUrlQueryStringAndFragment } from '../../shared/utils/remove-url-query-string-and-fragment'
import { PageWrapperWithMeta } from '../../shared/wrappers/PageWrapperWithMeta'
import styles from './template-styles/VerticalPage.module.scss'

const NewsListingPage: React.FC = () => {
  const { verticalSlug } = useParams<PageParams>()
  const { loading, error, data } = useNewsListingVerticalQuery({
    variables: { verticalSlug, pageNumber: 1 },
  })

  const { getFullUrl } = useFullUrl()
  const backStack = useBackStack()
  const previousUrl = getPreviousUrl({ backStack })
  const { getBaseMetaTags } = useBaseMetaTags()

  if (loading) {
    return <Loader />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (!data?.verticalPage) {
    return <PageError pageName="News Listing Vertical Page" />
  }

  const { meta, header, newsArticles, showResubBanner } = data.verticalPage

  const pageInfo: PageInfo = {
    wcdPageUrl: removeUrlQueryStringAndFragment(getFullUrl()),
    previousWcdPageUrl: previousUrl || '',
    vertical: '', // intentionally empty to follow previous news structure
    content_type: 'news',
    pageType: 'news-all',
  }

  const baseMetaTags = getBaseMetaTags({ meta })
  const metaTags = getMetaTags({ meta, baseMetaTags, pageInfo })

  return (
    <div data-testid="template-portal" data-template-type="news-listing-vertical">
      <ResubscribeBannerWrapper showBanner={showResubBanner} />
      <Breadcrumb
        heading={{ text: header.title }}
        currentPage={{ text: header.title, href: '' }}
        links={[]}
      />
      <PageWrapperWithMeta includePageWrapper={false} metaTags={metaTags}>
        <Grid>
          <GridItem span={{ medium: 10, large: 10 }} columnStart={{ medium: 2, large: 2 }}>
            <ArticleListingHeader
              name="Latest"
              count={newsArticles.pagedArticles.total}
              className={styles.articleListingHeader}
              articleTypeDescriptor="news"
            />
          </GridItem>
          <GridItem span={{ medium: 10, large: 10 }} columnStart={{ medium: 2, large: 2 }}>
            <FetchMore
              initialData={newsArticles.pagedArticles}
              lazyFetch={useNewsListingVerticalLazyQuery}
              lazyFetchVars={{ verticalSlug }}
              dataTarget="verticalPage.newsArticles.pagedArticles"
              dataKey="articles"
            >
              <GroupedArticles />
            </FetchMore>
          </GridItem>
        </Grid>
      </PageWrapperWithMeta>
    </div>
  )
}

export default NewsListingPage

export type NewsListingData = NewsListingVerticalQuery['verticalPage'] & {
  __typename: 'VerticalPage'
}

const getMetaTags = ({
  meta,
  baseMetaTags,
  pageInfo,
}: {
  meta: NewsListingData['meta']
  baseMetaTags: JSX.Element[]
  pageInfo: PageInfo
}) => {
  // data layer
  const dataLayer = getDataLayerScripts([...(meta.dataLayer || []), removeFalsy(pageInfo)])

  // doubleclick for publishers
  const dfpMeta = getDFPScripts(meta.dataLayer, pageInfo)

  return baseMetaTags.concat(dataLayer, dfpMeta)
}
